/**
 * SmartTable — shared table primitives for list views.
 * Used by: Leads (LeadsListViewV2), Bookings (BookingsListView), Inventory, Calls, etc.
 * Theme and styles live in smart_table_helper.js; this file provides the React components.
 */

import React from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import {
  TABLE_THEME,
  getTableTheme,
  getHeaderCellStyle,
  getBodyCellStyle,
  getLoadingStateContainerStyle,
  getEmptyStateCellStyle,
  TABLE_ROW_CLASS,
  TABLE_ROW_SELECTED_CLASS,
  TABLE_ROW_GLOBAL_CSS,
  TABLE_SCROLLBAR_CSS,
} from '../../utils/smart_table_helper';

// Re-export theme and styles for consumers that import from SmartTable
export {
  TABLE_THEME,
  TABLE_ROW_CLASS,
  TABLE_ROW_SELECTED_CLASS,
  TABLE_ROW_GLOBAL_CSS,
  TABLE_SCROLLBAR_CSS,
} from '../../utils/smart_table_helper';

// ─── Header cell ────────────────────────────────────────────────────────────
export function SmartTableTH({ children, width, align, style = {}, theme }) {
  const baseStyle = getHeaderCellStyle(theme, { width, align });
  return <th style={{ ...baseStyle, ...style }}>{children}</th>;
}

// ─── Body cell ──────────────────────────────────────────────────────────────
export function SmartTableTD({ children, style = {}, align, wrap, theme, ...rest }) {
  const baseStyle = getBodyCellStyle(theme, { align, wrap });
  return <td style={{ ...baseStyle, ...style }} {...rest}>{children}</td>;
}

// ─── Loading state (full-width row or block) ────────────────────────────────
export function TableLoadingState({ message = 'Loading…', theme, size = 32 }) {
  const resolvedTheme = getTableTheme(theme);
  const containerStyle = getLoadingStateContainerStyle();
  return (
    <Box sx={containerStyle}>
      <CircularProgress size={size} sx={{ color: resolvedTheme.green }} />
      <Typography sx={{ fontSize: 13, color: resolvedTheme.subtle, fontWeight: 500 }}>{message}</Typography>
    </Box>
  );
}

// ─── Empty state (no rows) ───────────────────────────────────────────────────
export function TableEmptyState({
  message = 'No items found',
  description,
  icon = '📋',
  theme,
  colSpan = 100,
}) {
  const resolvedTheme = getTableTheme(theme);
  const cellStyle = getEmptyStateCellStyle(theme);
  return (
    <tr>
      <td colSpan={colSpan} style={cellStyle}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
          <div style={{ fontSize: 36 }}>{icon}</div>
          <div style={{ fontWeight: 700, color: resolvedTheme.ink2, fontSize: 14 }}>{message}</div>
          {description && (
            <div style={{ fontSize: 12, maxWidth: 300, lineHeight: 1.6 }}>{description}</div>
          )}
        </div>
      </td>
    </tr>
  );
}

export default {
  SmartTableTH,
  SmartTableTD,
  TableLoadingState,
  TableEmptyState,
  TABLE_THEME,
  TABLE_ROW_CLASS,
  TABLE_ROW_SELECTED_CLASS,
  TABLE_ROW_GLOBAL_CSS,
  TABLE_SCROLLBAR_CSS,
};
